import type {
  event_audio_statistic_data_change,
  event_network_quality_change,
  event_video_statistic_data_change,
} from '@zoom/videosdk';

import type { Primitive } from '../../../types';

type AudioQosData = Parameters<typeof event_audio_statistic_data_change>[0]['data'];
type VideoQosData = Parameters<typeof event_video_statistic_data_change>[0]['data'];
type NetworkQualityDirection = Parameters<typeof event_network_quality_change>[0]['type'];

// Zoom emits audio/video QoS data on separate events from network-quality-change,
// so we cache the latest sample in each direction (encode = send, decode = recv).
export type ZoomNetworkQualityStatsCache = {
  audio: { send?: AudioQosData; recv?: AudioQosData };
  video: { send?: VideoQosData; recv?: VideoQosData };
};

// Which Zoom event triggered the emission. Useful in RUM to tell apart snapshots
// driven by overall-level transitions from those driven by per-stream QoS ticks.
export type ZoomNetworkQualityTrigger =
  | 'audio-statistic-data-change'
  | 'video-statistic-data-change'
  | 'network-quality-change';

// Payload shape the Zoom LocalParticipant emits via `networkQualityLevelChanged`.
// Mirrors Twilio's stats signature so the logging hook can flatten with a single
// helper call regardless of provider.
export type ZoomNetworkQualityStatsPayload = {
  cache: ZoomNetworkQualityStatsCache;
  level: number;
  direction: NetworkQualityDirection;
  trigger: ZoomNetworkQualityTrigger;
};

export function createZoomNetworkQualityStatsCache(): ZoomNetworkQualityStatsCache {
  return { audio: {}, video: {} };
}

export function updateAudioStats(cache: ZoomNetworkQualityStatsCache, data: AudioQosData) {
  if (data.encoding) {
    cache.audio.send = data;
  } else {
    cache.audio.recv = data;
  }
}

export function updateVideoStats(cache: ZoomNetworkQualityStatsCache, data: VideoQosData) {
  if (data.encoding) {
    cache.video.send = data;
  } else {
    cache.video.recv = data;
  }
}

export function getZoomNetworkQualityStats(payload: ZoomNetworkQualityStatsPayload): Record<string, Primitive> {
  const { cache, level, direction, trigger } = payload;
  return {
    level,
    direction,
    trigger,

    'audio.recv.rtt': cache.audio.recv?.rtt,
    'audio.recv.jitter': cache.audio.recv?.jitter,
    'audio.recv.avgLoss': cache.audio.recv?.avg_loss,
    'audio.recv.maxLoss': cache.audio.recv?.max_loss,
    'audio.recv.sampleRate': cache.audio.recv?.sample_rate,
    'audio.recv.bandwidth': cache.audio.recv?.bandwidth,
    'audio.recv.bitrate': cache.audio.recv?.bitrate,

    'audio.send.rtt': cache.audio.send?.rtt,
    'audio.send.jitter': cache.audio.send?.jitter,
    'audio.send.avgLoss': cache.audio.send?.avg_loss,
    'audio.send.maxLoss': cache.audio.send?.max_loss,
    'audio.send.sampleRate': cache.audio.send?.sample_rate,
    'audio.send.bandwidth': cache.audio.send?.bandwidth,
    'audio.send.bitrate': cache.audio.send?.bitrate,

    'video.recv.rtt': cache.video.recv?.rtt,
    'video.recv.jitter': cache.video.recv?.jitter,
    'video.recv.avgLoss': cache.video.recv?.avg_loss,
    'video.recv.maxLoss': cache.video.recv?.max_loss,
    'video.recv.sampleRate': cache.video.recv?.sample_rate,
    'video.recv.bandwidth': cache.video.recv?.bandwidth,
    'video.recv.bitrate': cache.video.recv?.bitrate,
    'video.recv.width': cache.video.recv?.width,
    'video.recv.height': cache.video.recv?.height,
    'video.recv.fps': cache.video.recv?.fps,

    'video.send.rtt': cache.video.send?.rtt,
    'video.send.jitter': cache.video.send?.jitter,
    'video.send.avgLoss': cache.video.send?.avg_loss,
    'video.send.maxLoss': cache.video.send?.max_loss,
    'video.send.sampleRate': cache.video.send?.sample_rate,
    'video.send.bandwidth': cache.video.send?.bandwidth,
    'video.send.bitrate': cache.video.send?.bitrate,
    'video.send.width': cache.video.send?.width,
    'video.send.height': cache.video.send?.height,
    'video.send.fps': cache.video.send?.fps,
  };
}
